import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Container, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { setOrderType, setDeliveryAddress } from "../../redux/orderTypeSlice";
import { useLazyCheckDeliveryLocationQuery } from "../../redux/services/orderService";
import { RESTAURANT_ID } from "../../constants/constans";
import { toast } from "react-toastify";
import "./index.css";

const mapContainerStyle = {
    width: "100%",
    height: "calc(100vh - 360px)",
    borderRadius: "0",
};

const GOOGLE_MAPS_LIBRARIES: ("places" | "geometry" | "marker")[] = ["places", "geometry", "marker"];

interface LocationState {
    address: string;
    location: {
        lat: number;
        lng: number;
    };
    isInsideDeliveryZone: boolean;
}

const DeliveryMapConfirm: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const state = location.state as LocationState;

    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(null);
    const [currentAddress, setCurrentAddress] = useState<string>("");
    const [currentLocation, setCurrentLocation] = useState<google.maps.LatLngLiteral | null>(null);
    const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
    const [isCheckingZone, setIsCheckingZone] = useState(false);
    const [isInsideDeliveryZone, setIsInsideDeliveryZone] = useState<boolean>(true);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

    const [checkDeliveryLocation] = useLazyCheckDeliveryLocationQuery();
    const restaurantId = RESTAURANT_ID;

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey:
            process.env.REACT_APP_GOOGLE_MAPS_API_KEY! ||
            "AIzaSyD_zEfLidcypXTaAVeiQVX5PHlrcj6xEpM",
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    useEffect(() => {
        if (!state || !state.location) {
            // If no state, redirect back
            navigate("/order");
            return;
        }
        setMapCenter(state.location);
        setCurrentLocation(state.location);
        setCurrentAddress(state.address);
        setIsInsideDeliveryZone(state.isInsideDeliveryZone);
    }, [state, navigate]);

    // Reverse geocode location to get address
    const reverseGeocode = async (location: google.maps.LatLngLiteral) => {
        setIsReverseGeocoding(true);
        const geocoder = new window.google.maps.Geocoder();

        try {
            const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
                geocoder.geocode({ location }, (results, status) => {
                    if (status === "OK" && results) {
                        resolve(results);
                    } else {
                        reject(status);
                    }
                });
            });

            if (results && results[0]) {
                const address = results[0].formatted_address;
                setCurrentAddress(address);
            }
        } catch (error) {
            console.error("Reverse geocoding failed:", error);
            setCurrentAddress(`${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`);
        } finally {
            setIsReverseGeocoding(false);
        }
    };

    // Check delivery zone
    const checkDeliveryZone = async (location: google.maps.LatLngLiteral) => {
        if (!location.lat || !location.lng) return;

        setIsCheckingZone(true);
        try {
            const result = await checkDeliveryLocation({
                point: {
                    latitude: location.lat,
                    longitude: location.lng
                },
                restaurantId: restaurantId,
            }).unwrap();

            const isInside = result.isInsideDeliveryZone === true;
            setIsInsideDeliveryZone(isInside);

            if (!isInside) {
                toast.error("Sorry! The location you selected is out of delivery zone.", {
                    position: "top-center",
                    autoClose: 5000,
                });
            }
        } catch (error) {
            console.error('Error checking delivery zone:', error);
            setIsInsideDeliveryZone(false);
            toast.error("Unable to verify delivery zone. Please try again.");
        } finally {
            setIsCheckingZone(false);
        }
    };

    // Handle marker drag or map click
    const handleLocationChange = (newLocation: google.maps.LatLngLiteral) => {
        setCurrentLocation(newLocation);
        setMapCenter(newLocation);
        reverseGeocode(newLocation);
        checkDeliveryZone(newLocation);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleConfirmAndContinue = () => {
        // Validate delivery zone
        if (isInsideDeliveryZone === false) {
            toast.error("The selected location is outside our delivery zone. Please choose a different address.");
            return;
        }

        if (!currentLocation || !currentAddress) {
            toast.error("Please select a valid delivery location.");
            return;
        }

        // Save delivery address to Redux
        dispatch(setDeliveryAddress({
            address: currentAddress,
            location: {
                latitude: currentLocation.lat,
                longitude: currentLocation.lng
            },
            isInsideDeliveryZone: isInsideDeliveryZone
        }));

        // Set order type to Delivery
        dispatch(setOrderType({ orderType: "Delivery" }));

        // Navigate to menu page
        navigate("/order");
    };

    if (!isLoaded || !mapCenter) {
        return <div>Loading map...</div>;
    }

    return (
        <div className="delivery-map-confirm-page">
            <Container fluid className="p-0">
                <div className="delivery-map-header">
                    <div className="delivery-address-info">
                        <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-danger me-2" size="lg" />
                            <h5 className="mb-0">DELIVERY ADDRESS</h5>
                        </div>
                        {isReverseGeocoding ? (
                            <p className="address-text mb-0">
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Updating address...
                            </p>
                        ) : (
                            <p className="address-text mb-0">{currentAddress}</p>
                        )}
                        {isCheckingZone && (
                            <small className="text-warning d-block mt-1">
                                <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                Checking delivery zone...
                            </small>
                        )}
                        {!isCheckingZone && isInsideDeliveryZone === false && (
                            <small className="text-danger d-block mt-1">
                                ⚠️ This location is outside our delivery zone
                            </small>
                        )}
                    </div>
                </div>

                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={16}
                    onLoad={(map) => {
                        mapRef.current = map;

                        // Create AdvancedMarkerElement
                        if (mapCenter && window.google?.maps?.marker?.AdvancedMarkerElement) {
                            const pinElement = new window.google.maps.marker.PinElement({
                                background: isInsideDeliveryZone ? "#DC3545" : "#FFC107",
                                borderColor: "#FFFFFF",
                                glyphColor: "#FFFFFF",
                                scale: 1.5,
                            });

                            markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
                                map: map,
                                position: mapCenter,
                                content: pinElement.element,
                                title: "Delivery Location - Drag to adjust",
                                gmpDraggable: true,
                            });

                            // Add drag event listener
                            markerRef.current.addListener('dragend', (event: any) => {
                                if (event.latLng) {
                                    const newLocation = {
                                        lat: event.latLng.lat(),
                                        lng: event.latLng.lng()
                                    };
                                    handleLocationChange(newLocation);
                                }
                            });
                        }
                    }}
                    onUnmount={() => {
                        if (markerRef.current) {
                            markerRef.current.map = null;
                            markerRef.current = null;
                        }
                    }}
                    onClick={(e) => {
                        if (e.latLng) {
                            const newLocation = {
                                lat: e.latLng.lat(),
                                lng: e.latLng.lng()
                            };
                            handleLocationChange(newLocation);

                            // Update marker position
                            if (markerRef.current) {
                                markerRef.current.position = newLocation;
                            }
                        }
                    }}
                    options={{
                        disableDefaultUI: false,
                        clickableIcons: false,
                        zoomControl: true,
                        streetViewControl: false,
                        fullscreenControl: true,
                        mapTypeControl: false,
                        mapId: "delivery-confirm-map"
                    }}
                >
                </GoogleMap>

                <div className="delivery-map-footer">
                    <p className="delivery-note">
                        The order will be delivered to the above location marked by the PIN. Please relocate the PIN if it's at the incorrect location
                    </p>
                    <div className="d-flex gap-3">
                        <Button
                            variant="warning"
                            className="back-btn"
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        <Button
                            variant="success"
                            className="confirm-btn flex-grow-1"
                            onClick={handleConfirmAndContinue}
                            disabled={isInsideDeliveryZone === false || isCheckingZone || isReverseGeocoding}
                        >
                            {isCheckingZone || isReverseGeocoding ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Verifying...
                                </>
                            ) : (
                                "Confirm & Continue"
                            )}
                        </Button>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default DeliveryMapConfirm;
